import { useState } from 'react';
import { Modal, Button, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExternalLink, Copy, Check } from 'lucide-react';
import type { AgentPersonaResponse } from '../../../infrastructure/AIAgentRegistryClient';
import { formatDate } from '../../../utils/dateUtils';

const getNotionUrl = (pageId: string) => `https://notion.so/${pageId.replace(/-/g, '')}`;

const CopyButton = ({ text, label }: { text: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{copied ? 'Copied!' : `Copy ${label}`}</Tooltip>}>
      <Button variant="outline-secondary" size="sm" className="ms-2" onClick={handleCopy}>
        {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />}
      </Button>
    </OverlayTrigger>
  );
};

interface AgentDetailModalProps {
  show: boolean;
  agent: AgentPersonaResponse | null;
  onHide: () => void;
  onEdit: () => void;
}

export const AgentDetailModal = ({ show, agent, onHide, onEdit }: AgentDetailModalProps) => {
  if (!agent) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {agent.name}
          <Badge bg={agent.type === 'supervisor' ? 'primary' : 'info'} className="ms-2">
            {agent.type}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Agent ID:</strong>
          <div className="d-flex align-items-center">
            <code className="bg-light px-2 py-1 rounded">{agent.id}</code>
            <CopyButton text={agent.id} label="Agent ID" />
          </div>
        </div>

        <div className="mb-3">
          <strong>Description:</strong>
          <p className="text-muted">{agent.description}</p>
        </div>

        <div className="mb-3">
          <strong>Notion Page:</strong>
          <div>
            <a href={getNotionUrl(agent.pageId)} target="_blank" rel="noopener noreferrer" className="d-inline-flex align-items-center">
              Open in Notion <ExternalLink size={14} className="ms-1" />
            </a>
          </div>
        </div>

        {agent.manualId && (
          <div className="mb-3">
            <strong>Linked Manual:</strong>
            <div>
              <a href={getNotionUrl(agent.manualId)} target="_blank" rel="noopener noreferrer" className="d-inline-flex align-items-center">
                Open Manual in Notion <ExternalLink size={14} className="ms-1" />
              </a>
            </div>
          </div>
        )}

        <div className="mb-3">
          <small className="text-muted">Updated: {formatDate(agent.updatedAt)}</small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" onClick={onEdit}>Edit</Button>
      </Modal.Footer>
    </Modal>
  );
};

